import React from "react";
import { AlertTriangle, Check, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// P1-1 — Material dot opens the same style of list as Doc Control: one row per PO, ✓ when on hand.
const DOT = { green: "bg-emerald-500", yellow: "bg-amber-400", red: "bg-red-500", grey: "bg-gray-300 dark:bg-gray-600" };
const HEAD = { green: "On hand", yellow: "Ordered — not everything is here", red: "No PO issued", grey: "No FRS rows to measure" };
const md = (iso) => (iso && iso.length >= 10 ? `${+iso.slice(5, 7)}/${+iso.slice(8, 10)}` : "");
const n = (v) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

const poText = (p) => [
  `${p.stage || p.status}`,
  p.received_at ? `received ${md(p.received_at)}` : p.shipped_on ? `shipped ${md(p.shipped_on)}` : p.ship_date ? `ships ${md(p.ship_date)}` : "",
  p.required_by ? `required ${md(p.required_by)}` : "",
  p.lines ? `${n(p.units_received)} of ${n(p.units_ordered)} units` : "",
].filter(Boolean).join(" · ");

/** groups = [{ label, colour, detail: material_detail }] — one for a scope, one per scope for the job. */
export function MaterialChecks({ colour, groups, testId, label = "" }) {
  if (colour === "grey" || !groups?.length) {
    return <span className={`mx-auto block h-2.5 w-2.5 rounded-full ${DOT.grey}`} data-testid={testId} data-colour="grey" title={HEAD.grey} />;
  }
  const pos = groups.flatMap((g) => g.detail?.pos || []);
  const have = pos.filter((p) => p.on_hand).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} data-testid={testId} data-colour={colour}
                title={`Material · ${HEAD[colour]} — click for the POs`}
                className={`mx-auto block h-2.5 w-2.5 rounded-full ${DOT[colour]} ring-offset-1 hover:ring-2 hover:ring-gray-300`} />
      </PopoverTrigger>
      <PopoverContent align="center" className="w-96 p-3" data-testid={`${testId}-pop`} onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-semibold text-foreground">
          Material{label ? ` — ${label}` : ""} · {have} of {pos.length} PO{pos.length === 1 ? "" : "s"} on hand
        </div>
        <div className="mt-2 max-h-72 space-y-2 overflow-y-auto">
          {groups.map((g) => (
            <div key={g.label} data-testid={`${testId}-scope-${g.label}`}>
              <div className="text-[11px] font-medium text-muted-foreground">
                {groups.length > 1 ? `${g.label} · ` : ""}On hand {n(g.detail?.on_hand)} of {n(g.detail?.required)} units
              </div>
              <ul className="mt-1 space-y-1">
                {(g.detail?.pos || []).length === 0 && (
                  <li className="flex items-start gap-2 text-xs text-red-700 dark:text-red-400">
                    <X className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={3} /> No PO issued against this scope
                  </li>
                )}
                {(g.detail?.pos || []).map((p) => (
                  <li key={`${g.label}-${p.number}`} className="flex items-start gap-2 text-xs" data-testid={`${testId}-po-${p.number}`} data-ok={p.on_hand ? "1" : "0"}>
                    {p.on_hand ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={3} />
                               : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" strokeWidth={3} />}
                    <span className={`w-28 shrink-0 font-medium ${p.on_hand ? "text-foreground" : "text-red-700 dark:text-red-400"}`}>PO {p.number || "—"}</span>
                    <span className="text-muted-foreground">{poText(p)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** P1-6 — red "!" with the reasons, left of the scope name and on the job row. */
export function RiskBadge({ reasons, testId, label = "" }) {
  if (!reasons?.length) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} data-testid={testId}
                title={`At risk:\n${reasons.join("\n")}`}
                className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50">
          <AlertTriangle className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3" data-testid={`${testId}-pop`} onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-semibold text-red-700 dark:text-red-400">At risk{label ? ` — ${label}` : ""}</div>
        <ul className="mt-1.5 list-disc space-y-1 pl-4 text-xs text-foreground">
          {reasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
